import { Box, Typography } from "@mui/material";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as InfinityIcon } from "components/Icons/infinity.svg";
import { BarChartPercentage } from "components/Table/BarChartPercentage";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { defaultOLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { formatNumberValue } from "utilities/numbers";
import {
  AVAILABILITY_LIMIT_WARNING_VALUE,
  countAvailabilityValue,
  countUsageGeneralProgressValue,
  countUsageLeftProgressValue,
  formatUsageLeft,
  formatUsageLimitGeneral,
  isGeneralUsageUnlimited,
  isUsageLeftUnlimited,
} from "../helpers";
import { useStyles } from "./AvailabilityColumn.styles";

interface Props {
  usageLeft: number | undefined;
  usageLimitGeneral: number | undefined;
  active: boolean;
}

export const AvailabilityColumn = ({
  usageLeft,
  usageLimitGeneral,
  active,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  const formattedUsageLeft = formatUsageLeft(usageLeft);
  const formattedUsageLimitGeneral = formatUsageLimitGeneral(
    usageLimitGeneral as number,
  );
  const availabilityValue = countAvailabilityValue(
    formattedUsageLeft,
    formattedUsageLimitGeneral,
  );
  const isWarningTooltipActive =
    typeof availabilityValue === "number" &&
    availabilityValue <= AVAILABILITY_LIMIT_WARNING_VALUE;
  const warningTooltipText = (
    <>
      <Typography className={classes.tooltipTitle}>
        {message({ id: "lowAvailability" })}
      </Typography>
      <Typography className={classes.tooltipDescription}>
        {message({ id: "lowAvailabilityInfo" })}
      </Typography>
    </>
  );

  return (
    <Box
      className={`${classes.rowItemsWrapper} ${classes.alignRight} ${classes.itemsGap}`}
    >
      {isWarningTooltipActive && (
        <TooltipWithIcon
          className={classes.tooltipIcon}
          icon={<AlertTriangleIcon className={classes.warningIcon} />}
          isWhite
          placement="bottom-start"
          text={warningTooltipText}
        />
      )}
      <Box className={classes.rowItemsWrapper}>
        {isUsageLeftUnlimited(usageLeft) ? (
          <InfinityIcon />
        ) : (
          formatNumberValue(usageLeft, "")
        )}
        {"/"}
        {isGeneralUsageUnlimited(usageLimitGeneral as number) ? (
          <InfinityIcon />
        ) : (
          formatNumberValue(usageLimitGeneral, "")
        )}
      </Box>
      <BarChartPercentage
        barChartData={[
          {
            itemShare: countUsageLeftProgressValue(
              formattedUsageLeft,
              formattedUsageLimitGeneral,
            ),
            itemColor: active
              ? defaultOLTheme.primary500
              : defaultOLTheme.gray500,
          },
          {
            itemShare: countUsageGeneralProgressValue(
              formattedUsageLeft,
              formattedUsageLimitGeneral,
            ),
            itemColor: active
              ? defaultOLTheme.primary200
              : defaultOLTheme.gray200,
          },
        ]}
        className={classes.barChart}
      />
    </Box>
  );
};
